import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Arrow, Communication, General } from 'icons/untitledui';
import {
  buildComposeUrl,
  EMAIL_PROVIDERS,
  PROVIDER_LABEL_KEYS,
} from 'shared/components/EmailComposeMenu/_index';
import { TopBarMenuItem } from 'topBar/components/TopBar';
import { EventDetail } from 'events/_types';
import { usePosthogCapture } from 'events/posthogCapture/_index';

interface UseEventVersionHistoryMenuItemsArgs {
  event: EventDetail;
  exportText: string;
}

export default function useEventVersionHistoryMenuItems({
  event,
  exportText,
}: UseEventVersionHistoryMenuItemsArgs): TopBarMenuItem[] {
  const { t } = useTranslation('pageEvents');
  const { t: tShared } = useTranslation('shared');
  const eventsCapture = usePosthogCapture();

  return useMemo(() => {
    if (!exportText) return [];

    const subject = t('eventVersionHistory.emailSubject', {
      title: event.title,
    });

    const sendSubItems: TopBarMenuItem[] = EMAIL_PROVIDERS.map((provider) => ({
      label: tShared(PROVIDER_LABEL_KEYS[provider]),
      icon: <Arrow.ArrowUpRight size={20} />,
      onClick: () => {
        window.open(
          buildComposeUrl(provider, { subject, body: exportText }),
          '_blank',
          'noopener,noreferrer'
        );
        eventsCapture.eventVersionHistoryEmailSent({
          provider,
          event_id: event.id,
        });
      },
    }));

    return [
      {
        label: t('eventVersionHistory.menuSend'),
        onClick: () => {},
        icon: <Communication.Send01 size={20} />,
        subItems: sendSubItems,
        subHeaderLabel: tShared('emailCompose.shareWith'),
      },
      {
        label: t('eventVersionHistory.menuCopy'),
        onClick: () => {
          navigator.clipboard.writeText(exportText).catch((err) => {
            console.error('Failed to copy version history: ', err);
          });
          eventsCapture.eventVersionHistoryCopied({ event_id: event.id });
        },
        icon: <General.Copy03 size={20} />,
        successMessage: t('eventVersionHistory.copySuccess'),
      },
    ];
  }, [event, exportText, t, tShared, eventsCapture]);
}
